/**
 * SS PDP section tabs — <ss-pdp-section-tabs>
 * Smooth-scrolls to the target section of each tab (matched by id, then by
 * class, then by data-section-type), keeps the tab of the section currently
 * in view active, shows a scroll-to-top arrow after the configured distance
 * and marks the bar when it is stuck under the header. No text or URLs here.
 */
(function () {
  'use strict';

  if (customElements.get('ss-pdp-section-tabs')) return;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function headerOffset(root) {
    var cssOffset = getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      || getComputedStyle(document.documentElement).getPropertyValue('--ss-header-offset');
    var header = parseFloat(cssOffset) || 0;
    var bar = root.querySelector('.tab-container');
    var sticky = root.dataset.sticky === 'true';
    return header + (sticky && bar ? bar.offsetHeight : 0) + (parseFloat(root.dataset.scrollOffset) || 0);
  }

  function findTarget(selector) {
    if (!selector) return null;
    var clean = selector.trim().replace(/^[#.]/, '');
    return document.getElementById(clean)
      || document.querySelector('.' + CSS.escape(clean))
      || document.querySelector('[data-section-type="' + clean + '"]')
      || document.querySelector('#shopify-section-' + CSS.escape(clean))
      || null;
  }

  function track(name, label) {
    try {
      if (typeof window.gaEvent === 'function') window.gaEvent(name, [label || '', '', '', '', '', ''], false);
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, custom: { param_1: label || '' } });
    } catch (e) { /* analytics must never break the page */ }
  }

  class SsPdpSectionTabs extends HTMLElement {
    constructor() {
      super();
      this._listeners = [];
      this._observer = null;
      this._raf = null;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.tab-button[data-scroll-to]'));
      this.topButton = this.querySelector('[data-ss-scroll-top]');
      this.bar = this.querySelector('.tab-container');
      this.targets = this.tabs.map((tab) => findTarget(tab.dataset.scrollTo));

      this.tabs.forEach((tab, index) => {
        this.on(tab, 'click', (event) => {
          event.preventDefault();
          this.scrollToIndex(index);
          track('pdp_tab', tab.dataset.trackLabel || tab.textContent.trim());
        });
        this.on(tab, 'keydown', (event) => this.onTabKeydown(event, index));
        if (!this.targets[index]) tab.setAttribute('aria-disabled', 'true');
      });

      if (this.topButton) {
        this.on(this.topButton, 'click', () => {
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          track('pdp_scroll_top', 'tabs');
        });
      }

      this.onScroll = this.onScroll.bind(this);
      this.on(window, 'scroll', this.onScroll, { passive: true });
      this.on(window, 'resize', this.onScroll, { passive: true });
      this.observeStuck();
      this.onScroll();
    }

    disconnectedCallback() {
      this._listeners.forEach(([el, type, fn, opts]) => el.removeEventListener(type, fn, opts));
      this._listeners = [];
      if (this._observer) this._observer.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    on(el, type, fn, opts) {
      el.addEventListener(type, fn, opts);
      this._listeners.push([el, type, fn, opts]);
    }

    /* Keyboard: arrows move between tabs, Enter/Space activates */
    onTabKeydown(event, index) {
      var next = null;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      this.tabs[next].focus();
    }

    scrollToIndex(index) {
      var target = this.targets[index];
      if (!target) return;
      var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset(this);
      window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      this.setActive(index);
      this.lockUntil = Date.now() + 800;
    }

    setActive(index) {
      this.tabs.forEach((tab, i) => {
        var active = i === index;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      var activeTab = this.tabs[index];
      if (activeTab && typeof activeTab.scrollIntoView === 'function') {
        activeTab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.updateTopButton();
        if (this.lockUntil && Date.now() < this.lockUntil) return;
        this.updateActiveFromScroll();
      });
    }

    updateTopButton() {
      if (!this.topButton || this.dataset.showTop !== 'true') return;
      var threshold = parseFloat(this.dataset.topThreshold) || 600;
      this.topButton.hidden = window.pageYOffset < threshold;
    }

    /* The active tab is the last target whose top passed the bar */
    updateActiveFromScroll() {
      var offset = headerOffset(this) + 8;
      var activeIndex = -1;
      this.targets.forEach((target, i) => {
        if (!target) return;
        if (target.getBoundingClientRect().top - offset <= 0) activeIndex = i;
      });
      if (activeIndex === -1) activeIndex = 0;
      if (!this.tabs[activeIndex].classList.contains('active')) {
        this.tabs.forEach((tab, i) => {
          var active = i === activeIndex;
          tab.classList.toggle('active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
        });
      }
    }

    /* Adds .is-stuck while the sticky bar is pinned (shadow in CSS) */
    observeStuck() {
      if (!('IntersectionObserver' in window) || this.dataset.sticky !== 'true') return;
      var sentinel = document.createElement('span');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;height:1px;width:1px;pointer-events:none;';
      this.insertBefore(sentinel, this.firstChild);
      this._observer = new IntersectionObserver((entries) => {
        this.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }, { rootMargin: '-' + Math.ceil(headerOffset(this)) + 'px 0px 0px 0px', threshold: 0 });
      this._observer.observe(sentinel);
    }
  }

  customElements.define('ss-pdp-section-tabs', SsPdpSectionTabs);
})();
